import { type LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import clsx from "clsx";
import { formatCurrency } from ".";

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  changeLabel?: string;
  secondaryChange?: number;
  secondaryLabel?: string;
  icon?: LucideIcon;
  iconColor?: string;
  format?: "number" | "currency" | "percent";
}

function formatValue(value: string | number, format?: string): string {
  if (typeof value === "string") return value;

  switch (format) {
    case "currency":
      return formatCurrency(value);
    case "percent":
      return `${value}%`;
    default:
      return value.toLocaleString("en-US");
  }
}

function getTrendColor(value: number): string {
  if (value > 0) return "text-green-600";
  if (value < 0) return "text-red-600";
  return "text-gray-500";
}

function TrendIcon({ value, className }: { value: number; className: string }) {
  if (value > 0) return <TrendingUp className={className} />;
  if (value < 0) return <TrendingDown className={className} />;
  return <Minus className={className} />;
}

function ChangeIndicator({
  value,
  label,
  showIcon,
}: {
  value: number;
  label: string;
  showIcon?: boolean;
}) {
  const color = getTrendColor(value);
  return (
    <div className="flex items-center gap-1">
      {showIcon && <TrendIcon value={value} className={clsx("h-4 w-4", color)} />}
      <span className={clsx("text-sm font-medium", color)}>
        {value > 0 ? "+" : ""}
        {value.toFixed(1)}%
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </div>
  );
}

export default function StatCard({
  title,
  value,
  subtitle,
  change,
  changeLabel = "MoM",
  secondaryChange,
  secondaryLabel = "YoY",
  icon: Icon,
  iconColor = "text-primary-500",
  format,
}: StatCardProps) {
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatValue(value, format)}
          </p>
          {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={clsx("rounded-lg bg-gray-100 p-3 dark:bg-gray-800", iconColor)}>
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>

      {(change !== undefined || secondaryChange !== undefined) && (
        <div className="mt-4 flex items-center gap-4">
          {change !== undefined && <ChangeIndicator value={change} label={changeLabel} showIcon />}
          {secondaryChange !== undefined && (
            <ChangeIndicator value={secondaryChange} label={secondaryLabel} />
          )}
        </div>
      )}
    </div>
  );
}
